import React, { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, AlertCircle, Circle, History, RotateCw } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import {
  crmGatewaySyncService,
  fetchBranchEntityStatuses,
  aggregateBranchStatus,
  HISTORICAL_SYNC_ENTITY_TYPES,
  HISTORICAL_SYNC_ENTITY_LABELS,
  type HistoricalSyncEntityType,
  type HistoricalSyncScope,
  type ReplayResponse,
} from "@/services/crmGatewaySyncService";

interface Props {
  open: boolean;
  /** Branch id, or the legal entity id when {@code scope} is LEGAL_ENTITY. */
  branchId: string;
  branchName: string;
  /**
   * Which entity types to replay, in order. Defaults to the branch-scoped four
   * (NEGOTIATOR → LEAD → CONTACT → PROPERTY); Propco passes its own set.
   */
  entityTypes?: readonly HistoricalSyncEntityType[];
  /**
   * LEGAL_ENTITY drives the replay off the legal entity instead of a branch —
   * `branchId` travels as `legalEntityId` and the request's branchId is null.
   */
  scope?: HistoricalSyncScope;
  /** Word for what's being synced in the header — "Branch" by default. */
  targetLabel?: string;
  /**
   * Latest aggregate historical-sync status for this branch, as last known to
   * the parent (COMPLETED, PAUSE, IN_PROGRESS, NOT_STARTED, …). When provided,
   * the modal reuses it on open and fires NO status call. When `undefined`, the
   * modal fetches once itself.
   */
  status?: string;
  /** Reports a freshly-fetched (or just-queued) status back up so the parent's
   * History icons stay in sync without a page reload. */
  onStatusChange?: (branchId: string, status: string) => void;
  onClose: () => void;
}

type StepStatus = "pending" | "running" | "done" | "error";

interface Step {
  entity: HistoricalSyncEntityType;
  status: StepStatus;
  result?: ReplayResponse;
  error?: string;
}

// The user is blocked from re-checking status for this long after each fetch.
const STATUS_REFRESH_COOLDOWN_MS = 10_000;

const initialSteps = (entityTypes: readonly HistoricalSyncEntityType[]): Step[] =>
  entityTypes.map((entity) => ({ entity, status: "pending" }));

/**
 * crm-gateway rejects a replay for an entity that's already fully synced with
 * `SYNC_ALREADY_COMPLETED` (HTTP 422). That's expected when a branch is only
 * partially synced, so we treat it as "this entity is done" rather than a
 * failure that aborts the whole run.
 */
const isAlreadyCompleted = (payload: { code?: string } | null | undefined): boolean =>
  String(payload?.code ?? "").toUpperCase() === "SYNC_ALREADY_COMPLETED";

const stepIcon = (status: StepStatus) => {
  switch (status) {
    case "done":
      return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
    case "running":
      return <Loader2 className="w-5 h-5 text-bridge-accent animate-spin" />;
    case "error":
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    default:
      return <Circle className="w-5 h-5 text-slate-300" />;
  }
};

const statusBadge = (status: unknown) => {
  // axios's response interceptor normalises errors so result.status can be
  // an HTTP status NUMBER (e.g. 400). Coerce defensively.
  const s = String(status ?? "").toUpperCase();
  let cls = "bg-slate-100 text-slate-600 border-slate-200";
  if (s === "COMPLETED" || s === "SUCCESS") cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (s === "PENDING" || s === "RUNNING" || s === "QUEUED" || s === "IN_PROGRESS")
    cls = "bg-blue-50 text-blue-700 border-blue-200";
  else if (s === "PAUSE" || s === "PAUSED") cls = "bg-amber-50 text-amber-700 border-amber-200";
  else if (s === "FAILED" || s === "ERROR") cls = "bg-red-50 text-red-700 border-red-200";
  return (
    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md border ${cls}`}>
      {s || "—"}
    </span>
  );
};

/**
 * Modal driving the four sequential crm-gateway /sync/replay calls
 * (NEGOTIATOR → LEAD → CONTACT → PROPERTY) for a single branch.
 *
 * The `from` / `to` window is decided server-side from the branch row
 * (branch.created_at → now), so this modal collects no input — it's a
 * confirm + progress surface. Each step shows crm-gateway's response
 * status pill and `jobsGenerated` so the operator knows whether the
 * replay landed and how many jobs were enqueued.
 *
 * There is no polling here: the modal reuses the parent's status on open (no
 * call), or fetches once if the parent has none, and lets the operator
 * re-check on demand via a Refresh button that is rate-limited to one call
 * every {@link STATUS_REFRESH_COOLDOWN_MS}.
 */
export const HistoricalSyncModal: React.FC<Props> = ({
  open,
  branchId,
  branchName,
  entityTypes = HISTORICAL_SYNC_ENTITY_TYPES,
  scope = "BRANCH",
  targetLabel = "Branch",
  status: statusProp,
  onStatusChange,
  onClose,
}) => {
  const isLegalEntityScope = scope === "LEGAL_ENTITY";
  const [steps, setSteps] = useState<Step[]>(() => initialSteps(entityTypes));
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  // Entities genuinely enqueued in the last run (0 when everything was already
  // synced) — drives whether we show the "queued" vs "already synced" message.
  const [queuedCount, setQueuedCount] = useState(0);
  const [status, setStatus] = useState<string | undefined>(statusProp);
  const [checking, setChecking] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  // Bumped by a timer so the cooldown countdown re-renders each second.
  const [, setTick] = useState(0);

  // Fetches this branch's per-entity status (four calls, one per entityType —
  // /sync-status requires entityType). Already-completed entities are pre-marked
  // "done" so runAll skips them and never fires a replay that would 422 with
  // SYNC_ALREADY_COMPLETED. Starts the Refresh cooldown once done so the
  // operator can't hammer the endpoint.
  const refreshStatus = async () => {
    setChecking(true);
    try {
      const byBranch = await fetchBranchEntityStatuses([branchId], { entityTypes, scope });
      const entityStatuses = byBranch[branchId] ?? {};
      const next = aggregateBranchStatus(entityStatuses, entityTypes);
      setStatus(next);
      onStatusChange?.(branchId, next);
      setSteps(
        entityTypes.map((entity) => {
          const s = String(entityStatuses[entity] ?? "NOT_STARTED").toUpperCase();
          const done = s === "COMPLETED" || s === "SUCCESS";
          return {
            entity,
            status: done ? "done" : "pending",
            result: done ? { status: s } : undefined,
          };
        }),
      );
    } catch {
      // Leave the previous status in place on a failed check.
    } finally {
      setChecking(false);
      setCooldownUntil(Date.now() + STATUS_REFRESH_COOLDOWN_MS);
    }
  };

  // On open: reset the run and seed the banner from the parent's aggregate
  // status (statusProp) — NO network call. The per-entity status calls fire
  // only when the operator clicks Refresh. Starting a run without refreshing is
  // still safe: runAll skips any entity crm-gateway reports as already
  // completed (SYNC_ALREADY_COMPLETED) instead of failing.
  useEffect(() => {
    if (!open) return;
    setSteps(initialSteps(entityTypes));
    setStarted(false);
    setRunning(false);
    setQueuedCount(0);
    setCooldownUntil(0);
    setStatus(statusProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId]);

  // Keep the cooldown countdown ticking while it's active.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const t = window.setTimeout(() => setTick((n) => n + 1), 400);
    return () => window.clearTimeout(t);
  });

  const { onOverlayClick } = useModalDismiss(open && !running, onClose);

  if (!open) return null;

  const updateStep = (entity: HistoricalSyncEntityType, patch: Partial<Step>) => {
    setSteps((prev) => prev.map((s) => (s.entity === entity ? { ...s, ...patch } : s)));
  };

  const runAll = async () => {
    setStarted(true);
    setRunning(true);
    let newlyQueued = 0;
    let errored = false;
    for (const entity of entityTypes) {
      // On a Retry, don't re-queue entities that already succeeded.
      if (steps.find((s) => s.entity === entity)?.status === "done") {
        continue;
      }

      updateStep(entity, { status: "running" });
      try {
        const result = await crmGatewaySyncService.replay(
          isLegalEntityScope
            ? { branchId: null, legalEntityId: branchId, entityType: entity }
            : { branchId, entityType: entity },
        );
        // A per-entity "already completed" isn't a failure: the aggregate
        // branch status can read not-yet-complete while some entities are
        // individually done. Mark it done and move on — but don't count it as
        // a job we queued.
        if (isAlreadyCompleted(result)) {
          updateStep(entity, { status: "done", result: { ...result, status: "COMPLETED" } });
          continue;
        }
        const upstreamFailed = ["FAILED", "ERROR"].includes(
          String(result?.status ?? "").toUpperCase(),
        );
        if (upstreamFailed) {
          updateStep(entity, {
            status: "error",
            result,
            error: result?.message ?? "Replay reported failure.",
          });
          errored = true;
          break;
        }
        newlyQueued += 1;
        updateStep(entity, { status: "done", result });
      } catch (err: any) {
        const data = err?.response?.data ?? err?.data ?? err;
        // Same as above — a 422 for an entity that's already synced just means
        // "skip this one", not "the whole run failed".
        if (isAlreadyCompleted(data)) {
          updateStep(entity, { status: "done", result: { ...data, status: "COMPLETED" } });
          continue;
        }
        updateStep(entity, {
          status: "error",
          result: data,
          error: data?.message ?? err?.message ?? "Sync failed",
        });
        errored = true;
        break;
      }
    }
    setRunning(false);
    setQueuedCount(newlyQueued);
    if (errored) return;
    if (newlyQueued > 0) {
      // The branch is now syncing — reflect that so the History icon disables
      // and a reopened modal shows the in-progress (blocked) state.
      setStatus("IN_PROGRESS");
      onStatusChange?.(branchId, "IN_PROGRESS");
    } else {
      // Nothing needed queuing — every entity was already synced.
      setStatus("COMPLETED");
      onStatusChange?.(branchId, "COMPLETED");
    }
  };

  const normalized = String(status ?? "").toUpperCase();
  // Show the spinner (and block Start) while the per-entity fetch is in flight,
  // so the operator can't start before completed entities are pre-marked done.
  const checkingReadiness = checking && !started;
  const loadFailed = !started && !checking && normalized === "";
  // Pre-run state, derived from the branch's aggregate status. Once a run has
  // started, the per-step UI below takes over and these are ignored.
  const alreadyCompleted =
    !started && !checkingReadiness && (normalized === "COMPLETED" || normalized === "SUCCESS");
  const isPaused =
    !started && !checkingReadiness && (normalized === "PAUSE" || normalized === "PAUSED");
  const isRemoteRunning =
    !started &&
    !checkingReadiness &&
    (normalized === "IN_PROGRESS" || normalized === "RUNNING" || normalized === "QUEUED");
  // A paused or already-running sync both block starting a fresh one.
  const blocked = isPaused || isRemoteRunning;
  const canStart =
    !started && !checkingReadiness && !loadFailed && !alreadyCompleted && !blocked;

  const anyError = steps.some((s) => s.status === "error");
  const allTerminal = steps.every((s) => s.status === "done" || s.status === "error");
  const finishedRun = started && !running && allTerminal;

  const cooldownRemaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
  const refreshDisabled = checking || cooldownRemaining > 0;
  const refreshLabel = checking
    ? "Checking…"
    : cooldownRemaining > 0
      ? `Refresh (${cooldownRemaining}s)`
      : "Refresh status";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink-900/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onOverlayClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-bridge-border bg-slate-50/60">
          <div>
            <h3 className="text-lg font-bold text-bridge-fg flex items-center gap-2">
              <History className="w-5 h-5 text-bridge-accent" />
              Sync Historical Data
            </h3>
            <p className="text-xs text-bridge-muted mt-0.5">
              {targetLabel} <strong className="text-bridge-fg">{branchName}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            aria-label="Close"
            className="p-1.5 text-bridge-muted hover:text-bridge-fg hover:bg-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {checkingReadiness && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin text-bridge-accent" />
              Checking sync status…
            </div>
          )}

          {loadFailed && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60">
              <AlertCircle className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-700">
                <div className="font-bold">Couldn't load sync status</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Use the Refresh button below to try again.
                </div>
              </div>
            </div>
          )}

          {alreadyCompleted && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50/60">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-800">
                <div className="font-bold">Historical sync already completed</div>
                <div className="text-xs text-emerald-700/80 mt-0.5">
                  All entities are already synced for this {targetLabel.toLowerCase()}. No further action is
                  required.
                </div>
              </div>
            </div>
          )}

          {isRemoteRunning && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-blue-200 bg-blue-50/60">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                <div className="font-bold flex items-center gap-2">
                  Historical sync in progress
                  {statusBadge(normalized)}
                </div>
                <div className="text-xs text-blue-700/80 mt-0.5">
                  One or more entities are currently being synced. Please wait for some time until
                  they finish before starting another sync.
                </div>
              </div>
            </div>
          )}

          {isPaused && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50/60">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-800">
                <div className="font-bold flex items-center gap-2">
                  Historical sync is paused
                  {statusBadge(normalized)}
                </div>
                <div className="text-xs text-amber-700/80 mt-0.5">
                  This {targetLabel.toLowerCase()}'s historical sync is currently paused. Please wait for some time and
                  check back later — you can't start a new sync until it clears.
                </div>
              </div>
            </div>
          )}

          {canStart && (
            <p className="text-sm text-slate-600">
              We'll replay the {entityTypes.length} entity types in order. Each call covers the full
              window from the {targetLabel.toLowerCase()}'s first record up to now.
            </p>
          )}

          {(canStart || started) && (
            <ol className="space-y-2">
              {steps.map((step) => (
                <li
                  key={step.entity}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                    step.status === "running"
                      ? "bg-blue-50/50 border-blue-100"
                      : step.status === "done"
                        ? "bg-emerald-50/50 border-emerald-100"
                        : step.status === "error"
                          ? "bg-red-50/50 border-red-100"
                          : "bg-white border-slate-100"
                  }`}
                >
                  <div className="shrink-0">{stepIcon(step.status)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-bridge-fg leading-tight">
                      {HISTORICAL_SYNC_ENTITY_LABELS[step.entity]}
                    </div>
                    <div className="text-[11px] text-bridge-muted font-mono">{step.entity}</div>
                    {step.error && (
                      <div
                        className="mt-1 text-[11px] text-red-600 line-clamp-2"
                        title={step.error}
                      >
                        {step.error}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {step.result?.jobsGenerated != null && (
                      <span className="text-[10px] font-bold text-bridge-muted">
                        {step.result.jobsGenerated} job{step.result.jobsGenerated === 1 ? "" : "s"}
                      </span>
                    )}
                    {step.result?.status && statusBadge(step.result.status)}
                  </div>
                </li>
              ))}
            </ol>
          )}

          {finishedRun && !anyError && queuedCount > 0 && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-blue-200 bg-blue-50/60 mt-4">
              <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                <div className="font-bold">Sync jobs have been queued</div>
                <div className="text-xs text-blue-700/80 mt-1">
                  The historical sync jobs have been successfully created and are running in the background.
                  Please wait for them to complete. You can safely close this window and check back here later to see if the sync has finished.
                </div>
              </div>
            </div>
          )}

          {finishedRun && !anyError && queuedCount === 0 && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50/60 mt-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-800">
                <div className="font-bold">Already synced</div>
                <div className="text-xs text-emerald-700/80 mt-1">
                  All entities were already synced for this {targetLabel.toLowerCase()}, so no new jobs were queued.
                  No further action is required.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-bridge-border flex items-center justify-end gap-3 bg-slate-50/40">
          {!started && (
            <Button
              variant="secondary"
              onClick={refreshStatus}
              disabled={refreshDisabled}
              className="rounded-xl h-10 px-4 mr-auto inline-flex items-center gap-2"
            >
              <RotateCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />
              {refreshLabel}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={running}
            className="rounded-xl h-10 px-4"
          >
            {started && !finishedRun ? "Cancel" : "Close"}
          </Button>
          {canStart && (
            <Button
              onClick={runAll}
              className="rounded-xl h-10 px-5 bg-bridge-accent hover:bg-bridge-accent/90 text-white font-bold inline-flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              Start Sync
            </Button>
          )}
          {finishedRun && anyError && (
            <Button
              onClick={runAll}
              className="rounded-xl h-10 px-5 bg-bridge-accent hover:bg-bridge-accent/90 text-white font-bold inline-flex items-center gap-2"
            >
              <RotateCw className="w-4 h-4" />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
